import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { NetworkStatusService } from './network-status.service';
import { HubRoutingService } from './hub-routing.service';
import { StorageService, StorageKey } from '../services/storage.service';
import { PosStateService } from '../../features/pos/pos-state.service';

/**
 * OFFLINE-AUTH-401 — an offline-minted session cannot survive reconnect.
 *
 * A PIN login performed while offline is answered by the hub, which mints a
 * JWT signed with a per-agent random key — deliberately opaque to the cloud
 * (`OfflineAuthService._mintOfflineJwt`). That is fine while routing points
 * at the hub, which does not validate bearers on its local routes.
 *
 * The moment routing flips back to the cloud, every call carries a token the
 * cloud cannot verify, and answers 401. `OfflineAuthService` assumed this
 * away — *"on reconnect, Angular's normal pin-login flow will mint a real
 * cloud JWT and replace this one"* — but nothing triggered that flow. So the
 * session was discovered to be invalid by a burst of 401s, and the error
 * interceptor tore it down, sometimes landing on the ADMIN login rather than
 * the POS PIN screen. Reported as "when syncing the app is logged out and
 * goes back to admin".
 *
 * This closes the loop deliberately: on the transition back to online, an
 * offline-minted token is retired and the operator is returned to the POS
 * PIN screen with a reason. Re-authenticating is the honest outcome — the
 * cloud genuinely never authenticated this person — but it should be a
 * controlled prompt, not a mystery logout.
 *
 * Why not exchange the token silently? Only the hub device runs the edge
 * bridge; a satellite tablet in a browser has no channel to exchange
 * through. A fix that works on one device class and not the other would
 * leave the tablets failing exactly as before, and harder to diagnose.
 * A silent exchange is still worth adding for hub devices later — as an
 * optimisation over this, not instead of it.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSessionGuardService {
  private net = inject(NetworkStatusService);
  private storage = inject(StorageService);
  private router = inject(Router);
  private injector = inject(Injector);

  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.net.state$
      .pipe(
        distinctUntilChanged(),
        filter((s) => s === 'online'),
      )
      .subscribe(() => this.retireOfflineSession());
  }

  /** True when this POS token was minted by the hub, not the cloud. */
  isOfflineMinted(token: string | null): boolean {
    if (!token) return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    try {
      const pad = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(pad + '==='.slice((pad.length + 3) % 4)));
      return payload?.offline === true;
    } catch {
      // An unreadable token is not evidence of anything. Leave it alone and
      // let the normal 401 path handle it — guessing here would log out a
      // perfectly good cloud session over a parsing quirk.
      return false;
    }
  }

  private retireOfflineSession(): void {
    // OF-SWITCH — under ConfirmBothWays the terminal is still on the hub
    // until someone confirms Go Online, and that confirmation swaps the
    // session for a cloud one. Retiring it here would sign the cashier out
    // of a hub that is still serving them.
    let confirmedOffline = false;
    try {
      confirmedOffline = this.injector.get(HubRoutingService, null)?.isConfirmedOffline?.() === true;
    } catch {
      // Routing not constructible here (isolated tests) — the old behaviour.
    }
    if (confirmedOffline) return;
    this.retireIfOfflineMinted();
  }

  /**
   * OF-SWITCH — after a confirmed Go Online: if the session could not be
   * exchanged it is still hub-minted, and the cloud never authenticated it,
   * so the cashier signs in again exactly as before.
   */
  retireIfOfflineMinted(): void {
    const token = this.storage.getRaw(StorageKey.POS_TOKEN);
    if (!this.isOfflineMinted(token)) return;

    // eslint-disable-next-line no-console
    console.info(
      '[offline-auth] back online with a hub-minted session — the cloud ' +
      'never authenticated this operator, so a fresh PIN is required.',
    );

    this.storage.remove(StorageKey.POS_AUTH);
    this.storage.remove(StorageKey.POS_TOKEN);
    // STUCK-PIN — the in-memory session too, or the PIN screen still sees a
    // signed-in user and bounces between itself and /pos/terminal. Resolved
    // lazily: PosStateService sits under features/pos.
    try {
      this.injector.get(PosStateService, null)?.clearPosState();
    } catch {
      // PosStateService drops a session whose token left storage on next read.
    }

    // NEVER the admin login. The standalone code identifies which store's
    // PIN screen to return to and is deliberately left in place.
    const code = this.storage.getRaw(StorageKey.POS_STANDALONE_CODE);
    void this.router.navigate(code ? ['/pos/s', code] : ['/pos']);
  }
}
